import type { KullaniciItem } from "../../../../mocks/kullanicilar";

export interface OperatorCardUser extends KullaniciItem {
  username?: string;
  sonGiris?: string;
  hasLoginAccount?: boolean;
  accountId?: number;
  forcePasswordChange?: boolean;
  smsOperationalOptIn?: boolean | null;
  smsMarketingOptIn?: boolean | null;
}

interface OperatorMeta {
  role: OperatorCardUser["rol"];
  username: string;
  title: string;
  path: string;
  defaultPassword: string;
  color: string;
  bg: string;
  icon: string;
}

interface Props {
  items: Array<{
    meta: OperatorMeta;
    user?: OperatorCardUser | null;
  }>;
  onCopyUsername: (username: string) => void;
  onResetDefaultPassword: (user: OperatorCardUser) => void;
  onEdit: (user: OperatorCardUser) => void;
  onToggleDurum: (user: OperatorCardUser) => void;
}

function fmtDate(value?: string) {
  if (!value) return "Hiç giriş yapmadı";
  try {
    return new Date(value).toLocaleString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

export default function OperatorAccountsSection({
  items,
  onCopyUsername,
  onResetDefaultPassword,
  onEdit,
  onToggleDurum,
}: Props) {
  return (
    <div className="bg-farm-warm border border-farm-border rounded-farm p-3 sm:p-4 space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-farm-earth-pale text-farm-earth">
          <i className="ri-settings-3-line text-base" />
        </div>
        <div>
          <p className="font-heading font-semibold text-sm text-farm-brown">
            Operasyon İstasyon Hesapları
          </p>
          <p className="text-[11px] text-farm-muted">
            Sabit kullanıcı adları korunur. Admin şifreyi varsayılana döndürebilir veya hesabı pasife alabilir.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
        {items.map(({ meta, user }) => {
          const isActive = user?.durum === "Aktif";
          return (
            <div
              key={meta.username}
              className="rounded-farm border border-farm-border bg-white p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-xl ${meta.bg} ${meta.color}`}>
                    <i className={`${meta.icon} text-lg`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-farm-brown">{meta.title}</p>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${isActive ? 'bg-farm-green-pale text-farm-green border-farm-green/20' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-farm-green' : 'bg-gray-400'}`} />
                        {user?.durum ?? "Yok"}
                      </span>
                    </div>
                    <p className="text-[11px] text-farm-muted mt-0.5">{meta.path}</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => onCopyUsername(meta.username)}
                  className="h-8 px-2.5 rounded-lg border border-farm-border bg-farm-cream text-[11px] font-semibold text-farm-brown hover:border-farm-earth/40"
                  title="Kullanıcı adını kopyala"
                >
                  <i className="ri-file-copy-line mr-1" />
                  {meta.username}
                </button>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
                <div className="rounded-xl border border-farm-border bg-farm-cream px-3 py-2">
                  <p className="text-farm-muted">Varsayılan Şifre</p>
                  <p className="font-mono font-semibold text-farm-brown mt-1 truncate">{meta.defaultPassword}</p>
                </div>
                <div className="rounded-xl border border-farm-border bg-farm-cream px-3 py-2">
                  <p className="text-farm-muted">Son Giriş</p>
                  <p className="font-semibold text-farm-brown mt-1 break-words">{fmtDate(user?.sonGiris)}</p>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {user ? (
                  <>
                    <button
                      type="button"
                      onClick={() => onResetDefaultPassword(user)}
                      className="h-9 px-3 rounded-xl bg-farm-earth text-white text-xs font-semibold hover:bg-farm-brown"
                    >
                      <i className="ri-lock-password-line mr-1" />
                      Varsayılan Şifreye Döndür
                    </button>
                    <button
                      type="button"
                      onClick={() => onEdit(user)}
                      className="h-9 px-3 rounded-xl border border-farm-border bg-farm-cream text-xs font-semibold text-farm-brown hover:border-farm-earth/40"
                    >
                      <i className="ri-pencil-line mr-1" />
                      Hesabı Düzenle
                    </button>
                    <button
                      type="button"
                      onClick={() => onToggleDurum(user)}
                      className="h-9 px-3 rounded-xl border border-farm-border bg-white text-xs font-semibold text-farm-brown hover:border-farm-earth/40"
                    >
                      <i className={`mr-1 ${isActive ? 'ri-pause-circle-line' : 'ri-play-circle-line'}`} />
                      {isActive ? "Pasife Al" : "Aktife Al"}
                    </button>
                  </>
                ) : (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">
                    Bu operatör hesabı bulunamadı. API başlatıldığında otomatik oluşmalıdır.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
